import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { AlertCircle, ArrowRight, Check, ChevronDown, Columns3, ExternalLink, Filter, Loader2, RotateCcw, Search, ShieldCheck } from "lucide-react";
import { getGetBvaComparisonQueryKey, getGetBvaTransactionEvidenceQueryKey, useGetBvaComparison, useGetBvaTransactionEvidence } from "@workspace/api-client-react";
import { Shell, Tag, formatMoney, formatPercent, formatPeriod, formatVariance } from "@/components/tekledger-ui";
import { useBvaStorageScope } from "@/framework/microapp-context";
import { useWorkspaceCompany } from "@/hooks/use-workspace-company";

type HandoffRecord = {
  glAccount: string;
  accountName?: string | null;
  budget: number;
  actual: number;
  variance: number;
  status: string;
};

type ExplainAccount = HandoffRecord;
type Disposition = "Needs Explanation" | "Explained" | "Unexplained" | "Needs Evidence";
type Draft = {
  reason: string;
  nature: string;
  expectedToReverse: string;
  reversalPeriod: string;
  owner: string;
  narrative: string;
  status: Disposition;
  sharedGroupId?: string;
};
type ColumnKey = "select" | "glAccount" | "description" | "department" | "glAccountName" | "budget" | "actual" | "variance" | "variancePercent" | "flag" | "reason" | "nature" | "owner" | "explanation" | "status";
type SortKey = "status" | "absoluteVariance" | "variance" | "variancePercent" | "account" | "flag";

const columnLabels: Record<ColumnKey, string> = {
  select: "Select",
  glAccount: "GL Account",
  description: "Description / Spend Item",
  department: "Department",
  glAccountName: "GL Account Name",
  budget: "Budget",
  actual: "Actual",
  variance: "Variance",
  variancePercent: "Var %",
  flag: "Flag",
  reason: "Reason",
  nature: "Nature",
  owner: "Owner",
  explanation: "Explanation",
  status: "Explanation Status",
};

const defaultColumnVisibility: Record<ColumnKey, boolean> = {
  select: true,
  glAccount: true,
  description: true,
  department: false,
  glAccountName: false,
  budget: true,
  actual: true,
  variance: true,
  variancePercent: false,
  flag: true,
  reason: true,
  nature: false,
  owner: false,
  explanation: false,
  status: true,
};

const reasons = ["Timing", "Usage / Volume", "Price / Rate", "Headcount", "Vendor / Contract", "Project", "Accounting", "Budget Mapping", "Structural", "One-Time", "Other", "Unexplained"];
const natures = ["Temporary", "Structural", "One-Time", "Unknown"];
const reverseOptions = ["Yes", "No", "Partially", "Unknown"];
const statuses: Disposition[] = ["Needs Explanation", "Explained", "Unexplained", "Needs Evidence"];

function statusTone(status: Disposition) {
  if (status === "Explained") return "good";
  if (status === "Unexplained" || status === "Needs Evidence") return "bad";
  return "";
}

function defaultDraft(): Draft {
  return { reason: "Timing", nature: "Unknown", expectedToReverse: "Unknown", reversalPeriod: "", owner: "", narrative: "", status: "Needs Explanation" };
}

function editableDraft(value?: Draft): Draft {
  const next = value ?? defaultDraft();
  return next.status === "Needs Explanation" ? { ...next, status: "Explained" } : next;
}

function flagFor(account: ExplainAccount) {
  return account.status === "actual_only" ? "No Budget"
    : account.status === "budget_only" ? "No Actual"
      : Math.abs(account.variance) > 0 ? "Material" : "—";
}

function loadGridPreferences(key: string) {
  if (typeof window === "undefined") return { columns: defaultColumnVisibility, rows: 20, sort: "status" as SortKey };
  try {
    const saved = JSON.parse(window.localStorage.getItem(key) || "null");
    return {
      columns: { ...defaultColumnVisibility, ...(saved?.columns || {}) },
      rows: [10, 20, 50, 0].includes(saved?.rows) ? saved.rows : 20,
      sort: ["status", "absoluteVariance", "variance", "variancePercent", "account", "flag"].includes(saved?.sort) ? saved.sort as SortKey : "status" as SortKey,
    };
  } catch {
    return { columns: defaultColumnVisibility, rows: 20, sort: "status" as SortKey };
  }
}

export default function ExplainPage() {
  const { companyId } = useWorkspaceCompany();
  const storageScope = useBvaStorageScope();
  const search = useSearch();
  const [, setLocation] = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const fiscalYear = Number(params.get("fy") || "2026");
  const period = params.get("period") || "07";
  const department = params.get("department") || "643";
  const periodView: "current_month" | "ytd" = params.get("view") === "ytd" ? "ytd" : "current_month";
  const workload = params.get("workload") || (params.get("gls") ? "selected" : "empty");
  const handoffRecords = useMemo<HandoffRecord[]>(() => {
    const raw = params.get("records");
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(record => typeof record?.glAccount === "string") : [];
    } catch {
      return [];
    }
  }, [params]);
  const selectedIds = useMemo(
    () => handoffRecords.length > 0
      ? handoffRecords.map(record => record.glAccount)
      : (params.get("gls") || "").split(",").map(value => value.trim()).filter(Boolean),
    [handoffRecords, params],
  );
  const comparisonParams = { companyId, fiscalYear, throughPeriod: period, departmentCode: department, periodView } as const;
  const { data: bva, isLoading, isError } = useGetBvaComparison(comparisonParams, {
    query: { queryKey: getGetBvaComparisonQueryKey(comparisonParams) },
  });
  const accounts = useMemo<ExplainAccount[]>(() => selectedIds.map(id => {
    const source = bva?.accountCoverage.accounts.find(account => account.glAccount === id);
    const handoff = handoffRecords.find(record => record.glAccount === id);
    return source ? { ...source, status: source.status } : handoff;
  }).filter((account): account is ExplainAccount => Boolean(account)), [bva, handoffRecords, selectedIds]);
  const storageKey = `tekledger-explain-${storageScope}-${fiscalYear}-${period}-${department}-${periodView}`;
  const gridPreferenceKey = `tekledger-grid-ER-explain-${storageScope}-${department}`;
  const gridPreferences = useMemo(() => loadGridPreferences(gridPreferenceKey), [gridPreferenceKey]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<Disposition | "All">("Needs Explanation");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortKey>(gridPreferences.sort);
  const [rowsPerPage, setRowsPerPage] = useState(gridPreferences.rows);
  const [page, setPage] = useState(0);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [columnSearch, setColumnSearch] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<Record<ColumnKey, boolean>>(gridPreferences.columns);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showBasis, setShowBasis] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [overrideMode, setOverrideMode] = useState(false);
  const [forecastWarning, setForecastWarning] = useState(false);
  const [notice, setNotice] = useState("");
  const [draft, setDraft] = useState<Draft>(() => editableDraft());

  useEffect(() => {
    if (!accounts.length) return;
    setFocusedId(previous => previous && accounts.some(account => account.glAccount === previous) ? previous : accounts[0].glAccount);
    setDrafts(previous => {
      const next = { ...previous };
      accounts.forEach(account => { if (!next[account.glAccount]) next[account.glAccount] = defaultDraft(); });
      return next;
    });
  }, [accounts]);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      if (saved) setDrafts(JSON.parse(saved));
    } catch {
      setNotice("Saved explanation drafts could not be restored in this browser.");
    }
  }, [storageKey]);

  useEffect(() => {
    try {
      window.localStorage.setItem(gridPreferenceKey, JSON.stringify({ columns: columnVisibility, rows: rowsPerPage, sort: sortBy }));
    } catch {
      setNotice("Grid preferences could not be saved in this browser.");
    }
  }, [columnVisibility, gridPreferenceKey, rowsPerPage, sortBy]);

  const focusedAccount = accounts.find(account => account.glAccount === focusedId) ?? accounts[0];
  const isBulk = selection.size > 1;
  const editorIds = isBulk ? Array.from(selection) : focusedAccount ? [focusedAccount.glAccount] : [];
  const editorAccounts = accounts.filter(account => editorIds.includes(account.glAccount));
  const singleEditorId = !isBulk && editorAccounts.length === 1 ? editorAccounts[0].glAccount : null;
  const combinedAbsoluteVariance = editorAccounts.reduce((sum, account) => sum + Math.abs(account.variance), 0);
  const currentDraft = editorAccounts.length === 1 ? drafts[editorAccounts[0].glAccount] ?? defaultDraft() : draft;
  const evidenceParams = {
    companyId,
    fiscalYear,
    throughPeriod: period,
    departmentCode: department,
    glAccount: focusedAccount?.glAccount ?? "",
    periodView,
  };
  const { data: evidence, isLoading: evidenceLoading } = useGetBvaTransactionEvidence(evidenceParams, {
    query: {
      enabled: Boolean(showEvidence && focusedAccount?.glAccount),
      queryKey: getGetBvaTransactionEvidenceQueryKey(evidenceParams),
    },
  });

  useEffect(() => {
    if (singleEditorId) {
      setDraft(editableDraft(drafts[singleEditorId]));
      setOverrideMode(false);
    }
  }, [drafts, singleEditorId]);

  const visibleAccounts = accounts.filter(account => {
    const status = drafts[account.glAccount]?.status ?? "Needs Explanation";
    const matchesFilter = filter === "All" || status === filter;
    const normalizedQuery = query.trim().toLowerCase();
    const draftValue = drafts[account.glAccount] ?? defaultDraft();
    const searchable = [
      account.glAccount,
      account.accountName,
      department,
      flagFor(account),
      draftValue.reason,
      draftValue.nature,
      draftValue.owner,
      draftValue.narrative,
    ].filter(Boolean).join(" ").toLowerCase();
    const matchesQuery = !normalizedQuery || searchable.includes(normalizedQuery);
    return matchesFilter && matchesQuery;
  });
  const countByStatus = (status: Disposition) => accounts.filter(account => (drafts[account.glAccount]?.status ?? "Needs Explanation") === status).length;
  const remaining = countByStatus("Needs Explanation");
  const materialCount = accounts.length;
  const sortedAccounts = [...visibleAccounts].sort((left, right) => {
    const leftDraft = drafts[left.glAccount] ?? defaultDraft();
    const rightDraft = drafts[right.glAccount] ?? defaultDraft();
    if (sortBy === "status") {
      const statusOrder: Record<Disposition, number> = { "Needs Explanation": 0, "Needs Evidence": 1, Unexplained: 2, Explained: 3 };
      const difference = statusOrder[leftDraft.status] - statusOrder[rightDraft.status];
      if (difference) return difference;
      return Math.abs(right.variance) - Math.abs(left.variance);
    }
    if (sortBy === "absoluteVariance") return Math.abs(right.variance) - Math.abs(left.variance);
    if (sortBy === "variance") return right.variance - left.variance;
    if (sortBy === "variancePercent") {
      const leftPercent = left.budget === 0 ? 0 : Math.abs(left.variance / left.budget);
      const rightPercent = right.budget === 0 ? 0 : Math.abs(right.variance / right.budget);
      return rightPercent - leftPercent;
    }
    if (sortBy === "flag") return flagFor(left).localeCompare(flagFor(right));
    return left.glAccount.localeCompare(right.glAccount);
  });
  const pageCount = rowsPerPage === 0 ? 1 : Math.max(1, Math.ceil(sortedAccounts.length / rowsPerPage));
  const pagedAccounts = rowsPerPage === 0 ? sortedAccounts : sortedAccounts.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const selectVisible = () => setSelection(new Set(pagedAccounts.map(account => account.glAccount)));
  const selectBy = (predicate: (account: ExplainAccount) => boolean) => setSelection(new Set(accounts.filter(predicate).map(account => account.glAccount)));
  const focusedFlag = focusedAccount ? flagFor(focusedAccount) : "";
  const selectRelated = (kind: "vendor" | "gl" | "journal" | "flag" | "noBudget") => {
    if (kind === "vendor" || kind === "journal") return;
    if (kind === "gl" && focusedAccount) selectBy(account => account.glAccount === focusedAccount.glAccount);
    if (kind === "flag" && focusedFlag) selectBy(account => flagFor(account) === focusedFlag);
    if (kind === "noBudget") selectBy(account => account.status === "actual_only");
  };
  const resetGrid = () => {
    setColumnVisibility(defaultColumnVisibility);
    setRowsPerPage(20);
    setSortBy("status");
    setPage(0);
    setQuery("");
    setFilter(remaining > 0 ? "Needs Explanation" : "All");
    setColumnsOpen(false);
  };
  const updateColumn = (column: ColumnKey) => setColumnVisibility(previous => ({ ...previous, [column]: !previous[column] }));
  const availableColumns = (Object.keys(columnLabels) as ColumnKey[]).filter(column => columnLabels[column].toLowerCase().includes(columnSearch.trim().toLowerCase()));

  useEffect(() => {
    setPage(0);
  }, [filter, query, rowsPerPage, sortBy]);

  useEffect(() => {
    const moveFocus = (direction: number) => {
      const currentIndex = sortedAccounts.findIndex(account => account.glAccount === focusedId);
      const nextIndex = Math.max(0, Math.min(sortedAccounts.length - 1, (currentIndex < 0 ? 0 : currentIndex) + direction));
      const nextAccount = sortedAccounts[nextIndex];
      if (!nextAccount) return;
      setFocusedId(nextAccount.glAccount);
      if (rowsPerPage > 0) setPage(Math.floor(nextIndex / rowsPerPage));
      window.setTimeout(() => document.getElementById(`explain-row-${nextAccount.glAccount}`)?.scrollIntoView({ block: "nearest" }), 0);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, textarea, select, button") || target?.isContentEditable) return;
      if (event.key === "ArrowDown" || event.key.toLowerCase() === "j") {
        event.preventDefault();
        moveFocus(1);
      }
      if (event.key === "ArrowUp" || event.key.toLowerCase() === "k") {
        event.preventDefault();
        moveFocus(-1);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [focusedId, rowsPerPage, sortedAccounts]);
  const updateField = (field: keyof Draft, value: string) => {
    if (!isBulk && editorAccounts.length === 1) setDraft(previous => ({ ...previous, [field]: value }));
    else setDraft(previous => ({ ...previous, [field]: value }));
  };

  const commitDraft = (ids: string[], nextDraft: Draft, shared = false) => {
    const normalized = nextDraft.reason === "Unexplained" && nextDraft.status === "Explained"
      ? { ...nextDraft, status: "Unexplained" as Disposition }
      : nextDraft;
    setDrafts(previous => {
      const next = { ...previous };
      ids.forEach(id => { next[id] = { ...normalized, sharedGroupId: shared ? (normalized.sharedGroupId || `shared-${Date.now()}`) : undefined }; });
      try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* local persistence is best effort */ }
      return next;
    });
    setNotice(shared ? `Shared explanation linked to ${ids.length} variance records.` : "Explanation saved.");
  };

  const validDisposition = (value: Draft) => value.status === "Explained" || Boolean(value.owner.trim() || value.narrative.trim());
  const saveSingle = (nextAction: boolean) => {
    if (!focusedAccount) return;
    if (!validDisposition(draft)) {
      setNotice("Add an owner or follow-up note before recording uncertainty.");
      return;
    }
    commitDraft([focusedAccount.glAccount], draft);
    if (nextAction) {
      const nextAccount = sortedAccounts.find(account => (drafts[account.glAccount]?.status ?? "Needs Explanation") === "Needs Explanation" && account.glAccount !== focusedAccount.glAccount);
      setFocusedId(nextAccount?.glAccount ?? focusedAccount.glAccount);
      setSelection(new Set());
    }
  };

  const applyBulk = () => {
    if (!editorIds.length) return;
    if (!validDisposition(draft)) {
      setNotice("Add an owner or follow-up note before recording uncertainty.");
      return;
    }
    commitDraft(editorIds, draft, true);
    setSelection(new Set());
    setFocusedId(editorIds[0]);
  };

  const continueForecast = () => {
    if (remaining > 0) {
      setForecastWarning(true);
      return;
    }
    const forecastPayload = accounts.map(account => ({
      variance_id: account.glAccount,
      reason: drafts[account.glAccount]?.reason,
      nature: drafts[account.glAccount]?.nature,
      expected_to_reverse: drafts[account.glAccount]?.expectedToReverse,
      expected_reversal_period: drafts[account.glAccount]?.reversalPeriod || null,
      owner: drafts[account.glAccount]?.owner || null,
      status: drafts[account.glAccount]?.status,
      narrative: drafts[account.glAccount]?.narrative || null,
    }));
    const next = new URLSearchParams({
      fy: String(fiscalYear),
      period,
      department,
      view: periodView,
      explanations: JSON.stringify(forecastPayload),
      returnTo: `/explain${search}`,
    });
    setLocation(`/forecast?${next.toString()}`);
  };

  const goToVariance = (gl?: string) => {
    const next = new URLSearchParams({ fy: String(fiscalYear), period, department, view: periodView });
    if (gl) next.set("gl", gl);
    setLocation(`/variance?${next.toString()}`);
  };

  const visibleColumnKeys = (Object.keys(columnLabels) as ColumnKey[]).filter(column => columnVisibility[column]);
  const renderCell = (account: ExplainAccount, column: ColumnKey): ReactNode => {
    const accountDraft = drafts[account.glAccount] ?? defaultDraft();
    if (column === "select") {
      return <input aria-label={`Select ${account.glAccount}`} type="checkbox" checked={selection.has(account.glAccount)} onClick={event => event.stopPropagation()} onChange={() => setSelection(previous => { const next = new Set(previous); if (next.has(account.glAccount)) next.delete(account.glAccount); else next.add(account.glAccount); return next; })} />;
    }
    if (column === "glAccount") return <b>{account.glAccount}</b>;
    if (column === "description" || column === "glAccountName") return account.accountName || <span className="unavailable-value">Not available</span>;
    if (column === "department") return department;
    if (column === "budget") return formatMoney(account.budget, "USD", true);
    if (column === "actual") return formatMoney(account.actual, "USD", true);
    if (column === "variance") return <span className={account.variance > 0 ? "bad-text" : "good-text"}>{formatVariance(account.variance, "USD")}</span>;
    if (column === "variancePercent") return account.budget === 0 ? "—" : formatPercent(account.variance / account.budget * 100);
    if (column === "flag") {
      const flag = flagFor(account);
      return <span className={`explain-flag ${flag === "No Budget" ? "bad" : ""}`}>{flag}</span>;
    }
    if (column === "reason") return <>{accountDraft.reason || "—"}{accountDraft.sharedGroupId && <small>Shared</small>}</>;
    if (column === "nature") return accountDraft.nature || "—";
    if (column === "owner") return accountDraft.owner || "—";
    if (column === "explanation") return accountDraft.narrative || "—";
    return <Tag tone={statusTone(accountDraft.status)}>{accountDraft.status}</Tag>;
  };

  if (isLoading) {
    return <Shell active="Explain"><section className="tl-content"><div className="bva-loading" style={{ minHeight: 300 }}><Loader2 className="animate-spin" size={24} /> Loading explanation queue…</div></section></Shell>;
  }
  if (isError) {
    return <Shell active="Explain"><section className="tl-content"><div className="source-page-error"><AlertCircle size={15} /><div><b>Explanation queue could not be loaded</b><span>Return to Variance and try the handoff again.</span></div></div></section></Shell>;
  }
  if (!accounts.length) {
    return <Shell active="Explain"><section className="tl-content"><div className="card explain-empty"><ShieldCheck size={24} /><h3>{workload === "empty" ? "No explanation required" : "No variances received"}</h3><p>{workload === "empty" ? "This scope has no material variance records requiring explanation." : "Return to Variance to select source-backed records."}</p>{workload !== "empty" && <Link href={`/variance?fy=${fiscalYear}&period=${period}&department=${department}`} className="tl-btn primary">Return to Variance</Link>}</div></section></Shell>;
  }

  return (
    <Shell active="Explain">
      <section className="tl-content explain-page">
        <div className="explain-header">
          <div>
            <div className="eyebrow">Explain Variances</div>
            <h1>FY{fiscalYear} | {formatPeriod(`${fiscalYear}-${period}`)} | {department} {bva?.scope.departmentName || ""}</h1>
            <p className="sub">Apply a defensible reason to each exact source-backed variance from the Variance workbench.</p>
          </div>
          <div className="vh-workflow">
            <div className="wf-step"><span className="wf-dot" /> Variance</div>
            <div className="wf-step active"><span className="wf-dot" /> Explain</div>
            <div className="wf-step"><span className="wf-dot empty" /> Forecast</div>
            <div className="wf-step"><span className="wf-dot empty" /> Review</div>
          </div>
        </div>

        <div className="explain-status-summary">
          <span><b>{materialCount}</b> material</span>
          <span><b>{countByStatus("Explained")}</b> explained</span>
          <span><b>{remaining}</b> remaining</span>
          <span><b>{countByStatus("Unexplained")}</b> unresolved</span>
          <span><b>{countByStatus("Needs Evidence")}</b> needs evidence</span>
        </div>

        <div className="explain-filter-bar">
          <div className="explain-filters">
            {(["Needs Explanation", "Explained", "Unexplained", "Needs Evidence", "All"] as const).map(value => (
              <button key={value} className={filter === value ? "active" : ""} onClick={() => setFilter(value)}>{value}{value !== "All" && <b>{countByStatus(value)}</b>}</button>
            ))}
          </div>
          <label className="explain-search"><Search size={13} /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search account, description, reason, owner…" /></label>
        </div>

        <div className="explain-grid-controls">
          <div className="explain-control-group">
            <Filter size={13} />
            <select aria-label="Sort variances" value={sortBy} onChange={event => setSortBy(event.target.value as SortKey)}>
              <option value="status">Needs Explanation first</option>
              <option value="absoluteVariance">Absolute Variance</option>
              <option value="variance">Unfavorable first</option>
              <option value="variancePercent">Variance %</option>
              <option value="account">GL Account</option>
              <option value="flag">Flag</option>
            </select>
          </div>
          <div className="explain-columns-control">
            <button className="btn" onClick={() => setColumnsOpen(value => !value)}><Columns3 size={13} /> Columns</button>
            {columnsOpen && <div className="explain-columns-popover">
              <div className="eyebrow">Columns</div>
              <label className="explain-column-search"><Search size={12} /><input value={columnSearch} onChange={event => setColumnSearch(event.target.value)} placeholder="Search columns…" /></label>
              <div className="explain-column-list">
                {availableColumns.map(column => <label key={column}><input type="checkbox" checked={columnVisibility[column]} onChange={() => updateColumn(column)} />{columnLabels[column]}</label>)}
              </div>
              <div className="explain-column-actions">
                <button onClick={() => setColumnVisibility(Object.fromEntries((Object.keys(columnLabels) as ColumnKey[]).map(column => [column, true])) as Record<ColumnKey, boolean>)}>Show All</button>
                <button onClick={() => setColumnVisibility(Object.fromEntries((Object.keys(columnLabels) as ColumnKey[]).map(column => [column, column === "select" || column === "glAccount"])) as Record<ColumnKey, boolean>)}>Show None</button>
                <button onClick={() => setColumnVisibility(defaultColumnVisibility)}>Reset</button>
              </div>
              <p>Vendor, journal, product, project, PO, and invoice fields are not available in the current BvA account source.</p>
            </div>}
          </div>
          <button className="btn" onClick={resetGrid}><RotateCcw size={13} /> Reset</button>
          <label className="explain-rows-control">Rows
            <select value={rowsPerPage} onChange={event => setRowsPerPage(Number(event.target.value))}>
              <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option><option value={0}>All</option>
            </select>
          </label>
        </div>

        {notice && <div className="explain-notice"><Check size={14} />{notice}<button onClick={() => setNotice("")}>Dismiss</button></div>}

        {remaining === 0 && !showCompleted && <div className="explain-complete card">
          <div className="explain-complete-mark"><ShieldCheck size={22} /></div>
          <div>
            <div className="eyebrow">Explanation complete</div>
            <h2>{materialCount} of {materialCount} material variances dispositioned</h2>
            <p>All required {formatPeriod(`${fiscalYear}-${period}`)} variances have a disposition.</p>
            <div className="explain-complete-counts"><span>{countByStatus("Unexplained")} unresolved</span><span>{countByStatus("Needs Evidence")} needs evidence</span></div>
          </div>
          <div className="explain-complete-actions">
            <button className="btn" onClick={() => { setShowCompleted(true); setFilter("All"); }}>Review Explanations</button>
            <button className="tl-btn primary" onClick={continueForecast}>Continue to Forecast <ArrowRight size={13} /></button>
          </div>
        </div>}

        {(remaining > 0 || showCompleted) && <>
        <div className="explain-workbench">
          <div className="explain-queue card">
            <div className="card-head">
              <div><h3>Variance explanation queue</h3><span className="mini">{sortedAccounts.length} of {accounts.length} records · {periodView === "current_month" ? formatPeriod(`${fiscalYear}-${period}`) : `YTD through ${formatPeriod(`${fiscalYear}-${period}`)}`} · ↑↓ or J/K to focus</span></div>
            </div>
            <div className="explain-related-toolbar">
              <div><div className="eyebrow">Select related variances</div><span>Use checkboxes for a deliberate bulk explanation.</span></div>
              <div className="explain-related-actions">
                <button className="btn" onClick={selectVisible}>Select Visible</button>
                <button className="btn" disabled title="Vendor is unavailable in the current BvA source">Same Vendor</button>
                <button className="btn" disabled={!focusedAccount} onClick={() => selectRelated("gl")}>Same GL</button>
                <button className="btn" disabled title="Journal is unavailable in the current BvA source">Same Journal</button>
                <button className="btn" disabled={!focusedAccount} onClick={() => selectRelated("flag")}>Same Flag</button>
                <button className="btn" onClick={() => selectRelated("noBudget")}>No Budget</button>
              </div>
              {selection.size > 0 && <div className="explain-related-summary">
                <span><b>{selection.size} selected</b> · {formatMoney(accounts.filter(account => selection.has(account.glAccount)).reduce((sum, account) => sum + Math.abs(account.variance), 0))} absolute variance</span>
                <button className="btn" onClick={() => setSelection(new Set())}>Clear</button>
                <button className="tl-btn primary" onClick={() => document.querySelector<HTMLElement>(".explain-editor")?.focus()}>Explain {selection.size} Selected <ArrowRight size={13} /></button>
              </div>}
            </div>
            <div className="explain-table-wrap">
              <table className="explain-table">
                <thead><tr>{visibleColumnKeys.map(column => <th className={`column-${column}`} key={column}>{columnLabels[column]}</th>)}</tr></thead>
                <tbody>
                  {pagedAccounts.map(account => {
                    return <tr id={`explain-row-${account.glAccount}`} key={account.glAccount} className={`${focusedId === account.glAccount ? "focused" : ""} ${selection.has(account.glAccount) ? "selected" : ""}`} onClick={() => setFocusedId(account.glAccount)}>
                      {visibleColumnKeys.map(column => <td className={`column-${column}`} key={column}>{renderCell(account, column)}</td>)}
                    </tr>;
                  })}
                  {!pagedAccounts.length && <tr><td colSpan={visibleColumnKeys.length} className="explain-no-results">No variances match the current search and filters.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="explain-pagination">
              <span>{sortedAccounts.length ? `Rows ${rowsPerPage === 0 ? 1 : page * rowsPerPage + 1}-${rowsPerPage === 0 ? sortedAccounts.length : Math.min((page + 1) * rowsPerPage, sortedAccounts.length)} of ${sortedAccounts.length}` : "0 rows"}</span>
              <div><button disabled={page === 0} onClick={() => setPage(value => Math.max(0, value - 1))}>Previous</button><b>Page {Math.min(page + 1, pageCount)} / {pageCount}</b><button disabled={page >= pageCount - 1} onClick={() => setPage(value => Math.min(pageCount - 1, value + 1))}>Next</button></div>
            </div>
          </div>

          <aside className="explain-editor card sticky-panel" tabIndex={-1}>
            {editorAccounts.length === 1 && editorAccounts[0] ? (
              <>
                <div className="card-head"><div><div className="eyebrow">Explanation editor</div><h3>{editorAccounts[0].glAccount} · {editorAccounts[0].accountName || "Unmapped account"}</h3><span className="mini">Focused record · click a row to change focus</span></div><Tag tone={statusTone(currentDraft.status)}>{currentDraft.status}</Tag></div>
                <div className="explain-editor-metrics"><span>Budget <b>{formatMoney(editorAccounts[0].budget)}</b></span><span>Actual <b>{formatMoney(editorAccounts[0].actual)}</b></span><span>Variance <b className={editorAccounts[0].variance > 0 ? "bad-text" : "good-text"}>{formatVariance(editorAccounts[0].variance)}</b></span><span>Var % <b>{editorAccounts[0].budget === 0 ? "—" : formatPercent(editorAccounts[0].variance / editorAccounts[0].budget * 100)}</b></span></div>
                {currentDraft.status === "Explained" && !overrideMode ? <div className="explain-readonly">
                  <div className="eyebrow">Explanation</div>
                  <dl>
                    <div><dt>Reason</dt><dd>{currentDraft.reason}</dd></div>
                    <div><dt>Nature</dt><dd>{currentDraft.nature}</dd></div>
                    <div><dt>Expected to reverse</dt><dd>{currentDraft.expectedToReverse}</dd></div>
                    <div><dt>Owner</dt><dd>{currentDraft.owner || "—"}</dd></div>
                    <div><dt>Status</dt><dd>{currentDraft.status}</dd></div>
                  </dl>
                  <p>{currentDraft.narrative || "No narrative provided."}</p>
                  <button className="btn" onClick={() => setOverrideMode(true)}>Edit Explanation</button>
                </div> : <>
                  {currentDraft.sharedGroupId && <div className="explain-shared-note"><Check size={13} /> Shared explanation inherited <button className="btn" onClick={() => setOverrideMode(true)}>Override</button></div>}
                  <div className="explain-editor-fields">
                    <label>Reason<select value={draft.reason} onChange={event => updateField("reason", event.target.value)}>{reasons.map(value => <option key={value}>{value}</option>)}</select></label>
                    <label>Nature<select value={draft.nature} onChange={event => updateField("nature", event.target.value)}>{natures.map(value => <option key={value}>{value}</option>)}</select></label>
                    <label>Disposition<select value={draft.status} onChange={event => updateField("status", event.target.value)}>{statuses.filter(value => value !== "Needs Explanation").map(value => <option key={value}>{value}</option>)}</select></label>
                    <label>Expected to reverse<select value={draft.expectedToReverse} onChange={event => updateField("expectedToReverse", event.target.value)}>{reverseOptions.map(value => <option key={value}>{value}</option>)}</select></label>
                    {(draft.expectedToReverse === "Yes" || draft.expectedToReverse === "Partially") && <label>Expected reversal period<input value={draft.reversalPeriod} onChange={event => updateField("reversalPeriod", event.target.value)} placeholder="e.g. Aug 2026" /></label>}
                    <label>Owner<input value={draft.owner} onChange={event => updateField("owner", event.target.value)} placeholder="Assign an owner" /></label>
                    <label className="explain-text">Explanation<textarea value={draft.narrative} onChange={event => updateField("narrative", event.target.value)} placeholder={draft.status === "Needs Evidence" ? "Add a note about the evidence still needed." : "Write a short, defensible explanation."} /></label>
                  </div>
                  {overrideMode && currentDraft.sharedGroupId && <div className="explain-override-note">This override updates only {editorAccounts[0].glAccount}; other linked records keep the shared explanation.</div>}
                </>}
                <div className="explain-editor-actions"><button className="btn" onClick={() => { setShowBasis(value => !value); setShowEvidence(false); }}>View Budget Basis <ChevronDown size={13} /></button><button className="btn" onClick={() => { setShowEvidence(value => !value); setShowBasis(false); }}>View Certified Transactions <ChevronDown size={13} /></button></div>
                {showBasis && <div className="explain-source-drawer"><b>Budget basis</b><span>Published lifecycle · {bva?.basis.budgetVersion || "Current published revision"}</span><span>Source: {bva?.basis.budgetSourceId || "Published Budget"}</span></div>}
                {showEvidence && <div className="explain-source-drawer"><b>Certified transaction evidence</b><span>{evidence?.transactionCount ?? 0} transactions</span>{evidenceLoading ? <span>Loading certified evidence…</span> : evidence?.transactions?.length ? evidence.transactions.slice(0, 4).map((transaction, index) => <span key={`${transaction.period}-${index}`}>{transaction.periodLabel} · {transaction.description || "Certified transaction"} · {formatMoney(transaction.amount)}</span>) : <span>No certified transactions returned for this record.</span>}<button className="btn" onClick={() => goToVariance(editorAccounts[0].glAccount)}>Open full evidence in Variance <ExternalLink size={12} /></button></div>}
                {(currentDraft.status !== "Explained" || overrideMode) && <div className="explain-primary-actions"><button className="tl-btn primary" onClick={() => saveSingle(true)}><Check size={13} /> Save &amp; Next</button><button className="btn" onClick={() => saveSingle(false)}>Save</button><button className="btn evidence-action" onClick={() => { setDraft(previous => ({ ...previous, status: "Needs Evidence" })); setNotice("Needs Evidence selected. Add an owner or note, then save."); }}>Mark Needs Evidence</button></div>}
              </>
            ) : editorAccounts.length > 1 ? (
              <>
                <div className="card-head"><div><div className="eyebrow">Bulk explanation</div><h3>Apply to {editorAccounts.length} variances</h3><span className="mini">Each source-backed record remains independent.</span></div><Tag>{editorAccounts.length} selected</Tag></div>
                <div className="explain-bulk-total"><span>Combined absolute variance</span><b>{formatMoney(combinedAbsoluteVariance)}</b></div>
                <div className="explain-editor-fields">
                  <label>Reason<select value={draft.reason} onChange={event => updateField("reason", event.target.value)}>{reasons.map(value => <option key={value}>{value}</option>)}</select></label>
                  <label>Nature<select value={draft.nature} onChange={event => updateField("nature", event.target.value)}>{natures.map(value => <option key={value}>{value}</option>)}</select></label>
                  <label>Disposition<select value={draft.status} onChange={event => updateField("status", event.target.value)}>{statuses.filter(value => value !== "Needs Explanation").map(value => <option key={value}>{value}</option>)}</select></label>
                  <label>Expected to reverse<select value={draft.expectedToReverse} onChange={event => updateField("expectedToReverse", event.target.value)}>{reverseOptions.map(value => <option key={value}>{value}</option>)}</select></label>
                  {(draft.expectedToReverse === "Yes" || draft.expectedToReverse === "Partially") && <label>Expected reversal period<input value={draft.reversalPeriod} onChange={event => updateField("reversalPeriod", event.target.value)} placeholder="e.g. Aug 2026" /></label>}
                  <label>Owner<input value={draft.owner} onChange={event => updateField("owner", event.target.value)} placeholder="Assign an owner" /></label>
                  <label className="explain-text">Explanation<textarea value={draft.narrative} onChange={event => updateField("narrative", event.target.value)} placeholder="Write one explanation to link to each selected record." /></label>
                </div>
                <div className="explain-editor-actions"><button className="btn" onClick={() => setShowBasis(value => !value)}>View Budget Basis <ChevronDown size={13} /></button><button className="btn" onClick={() => goToVariance(editorAccounts[0].glAccount)}>View Certified Transactions <ExternalLink size={12} /></button></div>
                {showBasis && <div className="explain-source-drawer"><b>Budget basis</b><span>Published lifecycle · {bva?.basis.budgetVersion || "Current published revision"}</span><span>Source: {bva?.basis.budgetSourceId || "Published Budget"}</span></div>}
                <button className="tl-btn primary explain-apply-button" onClick={applyBulk}>Apply to {editorAccounts.length} Variances <ArrowRight size={13} /></button>
              </>
            ) : <div className="explain-editor-empty"><h3>Select a variance</h3><p>Click a row to focus it, or use checkboxes for a shared explanation.</p></div>}
          </aside>
        </div>

        <div className="explain-footer">
          <div><div className="eyebrow">Explanation progress</div><div className="explain-progress-line"><b>{countByStatus("Explained")} explained</b><span>·</span><b>{countByStatus("Unexplained")} unexplained</b><span>·</span><b>{countByStatus("Needs Evidence")} needs evidence</b><span>·</span><b>{remaining} remaining</b></div></div>
          <div className="explain-footer-actions">
            {remaining > 0 && <button className="btn" onClick={() => { setFilter("Needs Explanation"); setPage(0); }}>Show Remaining</button>}
            <button className="tl-btn primary" onClick={continueForecast}>Continue to Forecast <ArrowRight size={13} /></button>
          </div>
        </div>
        </>}
        {forecastWarning && <div className="handoff-backdrop"><div className="handoff-sheet"><div className="card-head"><div><div className="eyebrow">Disposition required</div><h3>{remaining} variances still need disposition</h3></div><button className="btn" onClick={() => setForecastWarning(false)}>Close</button></div><p className="sub">Record Explained, Unexplained with an owner or note, or Needs Evidence with an owner or note before continuing to Forecast.</p><div className="handoff-actions"><button className="tl-btn primary" onClick={() => { setFilter("Needs Explanation"); setForecastWarning(false); }}>Show Remaining</button><button className="btn" onClick={() => setForecastWarning(false)}>Continue Later</button></div></div></div>}
      </section>
    </Shell>
  );
}